
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import axios from 'axios';
import { IActionAccessLevel } from '@/store/modules/authModule';
import { EPermissionActions } from '@/model/EPermissionActions';
import { IShortChannel } from '@/model/shortInterfaces';
import { settings } from '@/plugins/settings';

const authModule    = namespace('authModule');
const channelModule = namespace('channelModule');

interface ISelectItem {
  name: string;
  id: string | number;
}

@Component({})
export default class ReportsGeneral extends Vue {
  @authModule.Action('fetchActionAccessLevels')
  fetchActionAccessLevels!: () => void;

  @authModule.Getter('actionAccessLevels')
  actionAccessLevels!: IActionAccessLevel[];

  @channelModule.Action('fetchChannels')
  fetchChannels!: () => void;

  @channelModule.Getter('channels')
  channels!: IShortChannel[];

  adConsentForm: { channelId: string | number | null; consentGiven: number } = {
    channelId: null,
    consentGiven: 1,
  };
  uptimeForm: { startDate: string; endDate: string } = { startDate: '', endDate: '' };
  registrationsForm: { startDate: string; endDate: string } = { startDate: '', endDate: '' };

  adConsentLoading: boolean = false;
  uptimeLoading: boolean    = false;
  registrationsLoading: boolean = false;
  errorMessage: string = '';

  get canSeeReports(): boolean {
    let canSee = false;
    this.actionAccessLevels.forEach((each: IActionAccessLevel) => {
      if (each.action === EPermissionActions.ACTION_REPORTS) {
        canSee = true;
      }
    });
    return canSee;
  }

  get channelOptions(): ISelectItem[] {
    const all: ISelectItem[] = [{ id: '-1', name: this.$t('reports.general.advertisingConsent.allChannels').toString() }];
    return all.concat(this.channels.map((c: IShortChannel) => ({ id: String(c.id), name: c.name })));
  }

  get consentOptions() {
    return [
      { value: 1, label: this.$t('reports.general.advertisingConsent.yes').toString() },
      { value: 0, label: this.$t('reports.general.advertisingConsent.no').toString() },
    ];
  }

  created() {
    this.fetchActionAccessLevels();
    this.fetchChannels();
  }

  async downloadAdvertisingConsent() {
    this.adConsentLoading = true;
    this.errorMessage = '';
    try {
      const response = await axios.get(settings.endpoint + 'reports/advertising-consent', {
        params: {
          channel_id: this.adConsentForm.channelId,
          advertising_consent_given: this.adConsentForm.consentGiven,
        },
        responseType: 'blob',
      });
      this.triggerDownload(response.data, this.getFilenameFromResponse(response, 'advertising-consent.csv'));
    } catch (e) {
      this.errorMessage = this.$t('reports.general.downloadError').toString();
    } finally {
      this.adConsentLoading = false;
    }
  }

  async downloadLockerUptime() {
    this.uptimeLoading = true;
    this.errorMessage = '';
    try {
      const response = await axios.get(settings.endpoint + 'reports/locker-uptime', {
        params: {
          start_date: this.uptimeForm.startDate,
          end_date: this.uptimeForm.endDate,
        },
        responseType: 'blob',
      });
      this.triggerDownload(response.data, this.getFilenameFromResponse(response, 'locker-uptime.csv'));
    } catch (e) {
      this.errorMessage = this.$t('reports.general.downloadError').toString();
    } finally {
      this.uptimeLoading = false;
    }
  }

  async downloadRegistrations() {
    this.registrationsLoading = true;
    this.errorMessage = '';
    try {
      const response = await axios.get(settings.endpoint + 'reports/registrations', {
        params: {
          start_date: this.registrationsForm.startDate,
          end_date: this.registrationsForm.endDate,
        },
        responseType: 'blob',
      });
      this.triggerDownload(response.data, this.getFilenameFromResponse(response, 'registrations.csv'));
    } catch (e) {
      this.errorMessage = this.$t('reports.general.downloadError').toString();
    } finally {
      this.registrationsLoading = false;
    }
  }

  private triggerDownload(blob: Blob, filename: string) {
    const url  = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href  = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private getFilenameFromResponse(response: any, fallback: string): string {
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return match ? match[1] : fallback;
  }
}
